import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { getTrainings, createTraining, updateTraining } from "../../../_api/Talent";
import { getUsers } from "../../../_api/Admin";
import { Button } from "../../../_helpers/components/Button";
import { SideBar } from "../../../_helpers/components/SideBar";

const TYPE_LABELS = { INTERNE: "Interne", EXTERNE: "Externe" };

const emptyForm = () => ({
    name: "",
    description: "",
    type: "INTERNE",
    duration_hours: "",
    trainer_name: "",
    trainer_user_id: "",
    provider: "",
    is_certified: false,
    certification_label: "",
});

export const Formations = () => {
    const [trainings, setTrainings] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [form, setForm] = useState(emptyForm());
    const [editing, setEditing] = useState(null);
    const [openSidebar, setOpenSidebar] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const fetch = () => {
        setLoading(true);
        Promise.all([getTrainings(), getUsers().catch(() => ({ data: [] }))])
            .then(([tRes, uRes]) => {
                setTrainings(tRes.data || []);
                setUsers(uRes.data || []);
            })
            .catch((err) => setError(err?.response?.data?.message || "Erreur"))
            .finally(() => setLoading(false));
    };

    useEffect(() => fetch(), []);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!form.name.trim()) return;
        setIsSubmitting(true);
        const payload = {
            ...form,
            duration_hours: form.duration_hours ? parseFloat(form.duration_hours) : undefined,
            trainer_user_id: form.type === "INTERNE" && form.trainer_user_id ? parseInt(form.trainer_user_id) : null,
            trainer_name: form.type === "EXTERNE" ? form.trainer_name : null,
            provider: form.type === "EXTERNE" ? form.provider : null,
            is_certified: !!form.is_certified,
        };
        if (editing) {
            updateTraining(editing.id, payload)
                .then(() => {
                    toast.success("Formation mise à jour.");
                    setEditing(null);
                    setForm(emptyForm());
                    setOpenSidebar(false);
                    fetch();
                })
                .catch((err) => toast.error(err?.response?.data?.message || "Erreur"))
                .finally(() => setIsSubmitting(false));
        } else {
            createTraining(payload)
                .then(() => {
                    toast.success("Formation créée.");
                    setForm(emptyForm());
                    setOpenSidebar(false);
                    fetch();
                })
                .catch((err) => toast.error(err?.response?.data?.message || "Erreur"))
                .finally(() => setIsSubmitting(false));
        }
    };

    const openNew = () => {
        setEditing(null);
        setForm(emptyForm());
        setOpenSidebar(true);
    };

    const startEdit = (t) => {
        setEditing(t);
        setForm({
            name: t.name || "",
            description: t.description || "",
            type: t.type || "INTERNE",
            duration_hours: t.duration_hours ?? "",
            trainer_name: t.trainer_name || "",
            trainer_user_id: t.trainer_user_id ?? "",
            provider: t.provider || "",
            is_certified: !!t.is_certified,
            certification_label: t.certification_label || "",
        });
        setOpenSidebar(true);
    };

    const cancelEdit = () => {
        setEditing(null);
        setForm(emptyForm());
        setOpenSidebar(false);
    };

    const activeUsers = users.filter((u) => u.activated !== false);

    if (loading) {
        return (
            <div className="min-h-full bg-gray-50 flex items-center justify-center py-20">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-app-primary mx-auto mb-4"></div>
                    <p className="text-gray-600 font-medium">Chargement...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-full bg-gray-50 flex items-center justify-center">
                <div className="text-center text-red-600">{error}</div>
            </div>
        );
    }

    const TrainingForm = () => (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                <input type="text" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-app-primary focus:border-app-primary" required placeholder="Nom de la formation" />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-app-primary focus:border-app-primary">
                    <option value="INTERNE">Interne</option>
                    <option value="EXTERNE">Externe</option>
                </select>
            </div>
            {form.type === "INTERNE" && (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Formateur interne</label>
                    <select value={form.trainer_user_id} onChange={(e) => setForm((f) => ({ ...f, trainer_user_id: e.target.value }))} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-app-primary focus:border-app-primary">
                        <option value="">— Aucun —</option>
                        {activeUsers.map((u) => (
                            <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>
                        ))}
                    </select>
                </div>
            )}
            {form.type === "EXTERNE" && (
                <>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Nom du formateur</label>
                        <input type="text" value={form.trainer_name} onChange={(e) => setForm((f) => ({ ...f, trainer_name: e.target.value }))} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-app-primary focus:border-app-primary" placeholder="Ex: Organisme XYZ" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Organisme / fournisseur</label>
                        <input type="text" value={form.provider} onChange={(e) => setForm((f) => ({ ...f, provider: e.target.value }))} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-app-primary focus:border-app-primary" placeholder="Ex: Cegos, AFPA" />
                    </div>
                </>
            )}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Durée (heures)</label>
                <input type="number" step="0.5" min="0" value={form.duration_hours} onChange={(e) => setForm((f) => ({ ...f, duration_hours: e.target.value }))} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-app-primary focus:border-app-primary" placeholder="Ex: 8" />
            </div>
            <div className="flex items-center gap-2">
                <input type="checkbox" id="is_certified" checked={form.is_certified} onChange={(e) => setForm((f) => ({ ...f, is_certified: e.target.checked }))} className="rounded border-gray-300 text-app-primary focus:ring-app-primary" />
                <label htmlFor="is_certified" className="text-sm font-medium text-gray-700">Formation certifiante</label>
            </div>
            {form.is_certified && (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nom de la certification</label>
                    <input type="text" value={form.certification_label} onChange={(e) => setForm((f) => ({ ...f, certification_label: e.target.value }))} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-app-primary focus:border-app-primary" placeholder="Ex: PMP, ISO 9001" />
                </div>
            )}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-app-primary focus:border-app-primary" rows={3} />
            </div>
            <div className="flex gap-2 pt-2">
                {editing && (
                    <button type="button" onClick={cancelEdit} className="flex-1 py-2 px-4 rounded-sm border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-50">
                        Annuler
                    </button>
                )}
                <Button
                    buttonType="submit"
                    name={editing ? "Enregistrer" : "Ajouter"}
                    loading={isSubmitting}
                    customCN="flex-1 py-2 px-4 cursor-pointer rounded-sm border-2 border-app-primary bg-app-primary hover:border-app-secondary hover:bg-app-secondary text-white font-bold"
                    type="custom"
                />
            </div>
        </form>
    );

    return (
        <div className="min-h-full bg-gray-50">
            <div className="bg-white border-b border-gray-200 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">Formations</h1>
                            <p className="mt-2 text-sm text-gray-600">Catalogue et gestion des formations</p>
                        </div>
                        <Button action={openNew} name="Nouvelle formation" />
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex items-center space-x-3">
                                <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-violet-500 to-violet-600 rounded-lg">
                                    <svg className="w-6 h-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v3m0 2.253v3m0-3v3m0-3c-1.658 0-3 1.342-3 3s1.342 3 3 3 3-1.342 3-3v-3m0 0c-1.658 0-3-1.342-3-3s1.342-3 3-3 3 1.342 3 3v3m0-3c-1.658 0-3 1.342-3 3s1.342 3 3 3 3-1.342 3-3v-3" />
                                    </svg>
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-gray-900">Catalogue des formations</h2>
                                    <p className="text-sm text-gray-500">{trainings.length} formation{trainings.length > 1 ? "s" : ""}</p>
                                </div>
                            </div>
                        </div>
                        <div className="p-6">
                            {trainings.length > 0 ? (
                                <ul className="space-y-3">
                                    {trainings.map((t) => (
                                        <li key={t.id} className="flex items-start justify-between gap-3 py-3 px-4 rounded-lg bg-gray-50 border border-gray-100 hover:bg-gray-100/50 transition-colors">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <p className="font-medium text-gray-900">{t.name}</p>
                                                    {t.is_certified && (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800">Certifiée</span>
                                                    )}
                                                </div>
                                                <p className="text-sm text-gray-600">{TYPE_LABELS[t.type] || t.type}</p>
                                                <div className="text-xs text-gray-500 mt-1 space-y-0.5">
                                                    {t.trainer_user_id && t.trainer_user && (
                                                        <p>Formateur : {t.trainer_user.first_name} {t.trainer_user.last_name}</p>
                                                    )}
                                                    {t.trainer_name && !t.trainer_user_id && <p>Formateur : {t.trainer_name}</p>}
                                                    {t.provider && <p>Organisme : {t.provider}</p>}
                                                    {t.certification_label && <p>Certification : {t.certification_label}</p>}
                                                    {t.duration_hours && <p>{t.duration_hours} h</p>}
                                                </div>
                                            </div>
                                            <button type="button" onClick={() => startEdit(t)} className="text-sm text-app-primary hover:text-app-secondary font-medium shrink-0">Modifier</button>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <div className="text-center py-8">
                                    <svg className="mx-auto h-12 w-12 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v3m0 2.253v3m0-3v3m0-3c-1.658 0-3 1.342-3 3s1.342 3 3 3 3-1.342 3-3v-3" />
                                    </svg>
                                    <h3 className="mt-4 text-sm font-medium text-gray-900">Aucune formation</h3>
                                    <p className="mt-2 text-sm text-gray-500">Cliquez sur « Nouvelle formation » pour commencer.</p>
                                </div>
                            )}
                        </div>
                </div>
            </div>

            <SideBar
                title={editing ? "Modifier la formation" : "Nouvelle formation"}
                content={<TrainingForm />}
                isOpen={openSidebar}
                onClose={() => { setOpenSidebar(false); setEditing(null); setForm(emptyForm()); }}
            />
        </div>
    );
};
